'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { APIStatus } from '@application/enums/api-status';
import { PriorityEnum } from '@application/enums/priority-enum';
import { TaskCompletionStatusEnum } from '@application/enums/task-completion-status-enum';
import { TaskType } from '@application/enums/task-type';
import { ManualActivityTypeEnum, ManualActivityTypeEnumDisplay } from '@application/enums/manual-activity-type-enum';
import { getPeopleActivitiesByPersonIdAsync } from '@application/features/peopleActivities/peopleActivitiesThunk';
import { createTaskAsync, updateTaskAsync } from '@application/features/task/taskThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import BDrawer from '@ui/components/common/BDrawer';
import { CustomDatePicker } from '@ui/components/common/DateTimePicker';
import CustomDialog from '@ui/components/common/Dialog';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
const AddNewActivityTaskDialog = ({ open, onClose, title, personId, familyMembers, editMode = false, taskData, onSuccess }) => {
    const { t } = useTranslation();
    const formRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(1280)); // <--- key change
    const dispatch = useAppDispatch();
    const { peopleActivitiesByPersonId, status: peopleActivitiesStatus } = useAppSelector((state) => state.PeopleActivity);
    const { usersWithoutPagination, status: usersStatus } = useAppSelector((state) => state.user);
    const personDetail = useAppSelector((state) => state.people.person);
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const isCollaborator = Array.isArray(currentUserRoles) && (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
        defaultValues: {
            name: '',
            activityType: null,
            activityId: '',
            manualActivityType: null,
            assignedTo: [],
            isActivityTask: false,
            priority: undefined,
            deadline: new Date().toISOString(),
            personId: personId,
            relationId: 'main'
        }
    });
    const selectedFamilyMember = watch('relationId');
    const selectedActivityId = watch('activityId');
    const selectedManualActivityType = watch('manualActivityType');
    const selectedActivityType = watch('activityType');
    // Fetch activities when dialog opens
    useEffect(() => {
        if (open && personId) {
            dispatch(getPeopleActivitiesByPersonIdAsync(personId));
        }
    }, [open, personId, dispatch]);
    // Handle activity type change: clear the opposite field when switching types
    useEffect(() => {
        if (selectedActivityType === 'planned') {
            setValue('manualActivityType', null);
        }
        else if (selectedActivityType === 'makeshift') {
            setValue('activityId', null);
        }
    }, [selectedActivityType, setValue]);
    // Populate form when in edit mode
    useEffect(() => {
        if (open) {
            if (editMode && taskData) {
                // Convert assignedTo string array to option format for the checkbox autocomplete
                const assignedToOptions = (taskData.assignedTo || []).map((userId) => {
                    const user = usersWithoutPagination?.find((u) => u.id === userId);
                    return {
                        label: user ? `${user.firstName} ${user.lastName}` : userId,
                        value: userId
                    };
                });
                // Determine activity type based on existing data
                const activityType = taskData.manualActivityType !== null && taskData.manualActivityType !== undefined
                    ? 'makeshift'
                    : taskData.activityId
                        ? 'planned'
                        : null;
                // Reset form with the task data
                reset({
                    name: taskData.name || '',
                    deadline: taskData.deadline,
                    assignedTo: assignedToOptions,
                    priority: taskData.priority,
                    activityType: activityType,
                    activityId: taskData.activityId || '',
                    manualActivityType: taskData.manualActivityType ?? null,
                    isActivityTask: !!taskData.activityId || !!taskData.manualActivityType,
                    // For main person selection, relationId is null → map to "main" to match option
                    relationId: taskData.relationId ?? 'main',
                    personId: personId
                });
                // Additional setValue calls to ensure values are set
                setTimeout(() => {
                    setValue('name', taskData.name || '');
                    setValue('deadline', taskData.deadline);
                    setValue('assignedTo', assignedToOptions);
                    setValue('priority', taskData.priority);
                    setValue('activityType', activityType);
                    setValue('activityId', taskData.activityId || '');
                    setValue('manualActivityType', taskData.manualActivityType ?? null);
                    setValue('isActivityTask', !!taskData.activityId || !!taskData.manualActivityType);
                    // Ensure the Link To shows main person when relationId is null
                    setValue('relationId', (taskData.relationId ?? 'main'));
                }, 100);
            }
            else if (!editMode) {
                // Reset form for create mode
                reset();
            }
        }
    }, [editMode, taskData, open, setValue, reset, familyMembers, personId, usersWithoutPagination]);
    const onSubmit = async (data) => {
        try {
            // Validate required fields
            if (!data.name ||
                !data.deadline ||
                data.priority === null ||
                data.priority === undefined ||
                data.relationId === null ||
                data.relationId === undefined) {
                toast.error(t('validations.requiredFields'));
                return;
            }
            // Validate activity type and activity selection
            if (!data.activityType) {
                toast.error(t('validations.selectActivityType') || 'Please select an activity type');
                return;
            }
            if (data.activityType === 'planned' && !data.activityId) {
                toast.error(t('validations.selectActivity') || 'Please select an activity');
                return;
            }
            if (data.activityType === 'makeshift' &&
                (data.manualActivityType === null || data.manualActivityType === undefined)) {
                toast.error(t('validations.selectMakeshiftActivity') || 'Please select a makeshift activity');
                return;
            }
            // Convert assignedTo from option format back to string array
            let assignedToIds = [];
            if (data.assignedTo) {
                if (Array.isArray(data.assignedTo)) {
                    assignedToIds = data.assignedTo
                        .map((item) => {
                        if (typeof item === 'string') {
                            return item;
                        }
                        else if (item && typeof item === 'object' && item.value) {
                            return item.value;
                        }
                        return null;
                    })
                        .filter((id) => id !== null);
                }
            }
            // Normalize relationId: "main" -> null, keep other values as is
            const normalizedRelationId = data.relationId === 'main' ? null : data.relationId;
            // Determine if this is an adhoc activity based on manualActivityType
            const isAdhocActivity = data.manualActivityType !== null && data.manualActivityType !== undefined;
            if (editMode && taskData) {
                // Update existing task
                const updateTaskData = {
                    id: taskData.id,
                    name: data.name,
                    assignedTo: assignedToIds.length > 0 ? assignedToIds : null,
                    deadline: data.deadline,
                    priority: data.priority,
                    completionStatus: taskData.completionStatus || TaskCompletionStatusEnum.New,
                    taskType: TaskType.People_Activity,
                    eventId: taskData.eventId || null,
                    isActivityTask: !!data.activityId || isAdhocActivity,
                    activityId: isAdhocActivity ? null : data.activityId || null,
                    manualActivityType: isAdhocActivity ? data.manualActivityType : null,
                    relationId: normalizedRelationId,
                    personId: personId,
                    collaboratorIds: taskData?.collaboratorIds || null,
                    CollaboratorTaskAccessLevel: taskData?.collaboratorTaskAccessLevel || undefined
                };
                await dispatch(updateTaskAsync(updateTaskData)).unwrap();
                toast.success(t('tasks.taskUpdatedSuccessfully'));
            }
            else {
                // Create new task
                const createTaskData = {
                    name: data.name,
                    assignedTo: assignedToIds.length > 0 ? assignedToIds : null,
                    deadline: data.deadline,
                    priority: data.priority,
                    taskType: TaskType.People_Activity,
                    eventId: null,
                    isActivityTask: !!data.activityId || isAdhocActivity,
                    activityId: isAdhocActivity ? null : data.activityId || null,
                    manualActivityType: isAdhocActivity ? data.manualActivityType : null,
                    relationId: normalizedRelationId,
                    personId: personId
                };
                await dispatch(createTaskAsync(createTaskData)).unwrap();
                toast.success(t('tasks.taskCreatedSuccessfully'));
            }
            reset();
            onSuccess?.();
            onClose();
        }
        catch (error) {
            console.error(`Failed to ${editMode ? 'update' : 'create'} task:`, error);
            toast.error(t(`tasks.failedTo${editMode ? 'Update' : 'Create'}Task`));
        }
    };
    const getPriorityTranslationKey = (priorityValue) => {
        switch (priorityValue) {
            case PriorityEnum.High:
                return 'priority.high';
            case PriorityEnum.Medium:
                return 'priority.medium';
            case PriorityEnum.Low:
                return 'priority.low';
            case PriorityEnum.None:
                return 'priority.none';
            default:
                return 'priority.medium';
        }
    };
    const getPriorityIcon = (priority) => {
        switch (priority) {
            case PriorityEnum.High:
                return (_jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.success.light }));
            case PriorityEnum.Medium:
                return (_jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.warning.light }));
            case PriorityEnum.Low:
                return _jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.grey[300] });
            default:
                return _jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.grey[800] });
        }
    };
    // Get deadline label based on selected makeshift activity type
    const getDeadlineLabel = () => {
        // Only change label if makeshift activity is selected
        if (selectedActivityType === 'makeshift' &&
            selectedManualActivityType !== null &&
            selectedManualActivityType !== undefined) {
            switch (selectedManualActivityType) {
                case ManualActivityTypeEnum.Resignation:
                    return t('tasks.lastDate') || 'Last Date';
                case ManualActivityTypeEnum.BirthAdoption:
                    return t('tasks.deadline') || 'Deadline';
                case ManualActivityTypeEnum.EarlyRetirement:
                    return t('tasks.lastDate') || 'Last Date';
                case ManualActivityTypeEnum.Sick:
                    return t('tasks.deadline') || 'Deadline';
                case ManualActivityTypeEnum.Funeral:
                    return t('tasks.funeralDate') || 'Funeral Date';
                case ManualActivityTypeEnum.MarriageRegistration:
                    return t('tasks.deadline') || 'Deadline';
                case ManualActivityTypeEnum.EmployeeOnboarding:
                    return t('tasks.employmentStartDate') || 'Employment Start Date';
                default:
                    return t('tasks.deadline') || 'Deadline';
            }
        }
        // Default label for planned activities or when no makeshift activity is selected
        return t('tasks.deadline') || 'Deadline';
    };
    const formContent = (_jsx("form", { ref: formRef, onSubmit: handleSubmit(onSubmit), children: _jsxs(Box, { sx: { p: isMobile ? 0 : 3, gap: 1.5, display: 'flex', flexDirection: 'column' }, children: [_jsx(ControlledFilledTextField, { control: control, bg: 'grey8', name: 'name', label: t('tasks.taskName'), errorMessage: errors.name?.message, fullWidth: true, margin: 'normal', rules: {
                        required: t('validations.required', { field: t('tasks.name') }),
                        minLength: {
                            value: 3,
                            message: t('validations.minLength', {
                                field: t('tasks.name'),
                                min: 3
                            })
                        },
                        maxLength: {
                            value: 100,
                            message: t('validations.maxLength', {
                                field: t('tasks.name'),
                                max: 100
                            })
                        }
                    } }), _jsx(CustomDatePicker, { bg: 'grey8', control: control, name: 'deadline', error: errors?.deadline?.message, label: getDeadlineLabel(), rules: {
                        required: t('validations.required', { field: getDeadlineLabel() })
                    } }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'relationId', label: t('tasks.linkTo'), options: [
                        // Main person option: use "main" as distinct identifier
                        ...(personDetail?.name ? [{ label: personDetail.name, value: 'main' }] : []),
                        ...(familyMembers ?? []).map((member) => ({
                            label: `${member.name}${member.relationship ? ` (${member.relationship} of ${personDetail?.name || ''})` : ''}`,
                            value: member.id
                        }))
                    ], error: errors.relationId?.message, rules: {
                        required: t('validations.required', { field: t('tasks.linkTo') }),
                        validate: (value) => {
                            // Allow "main" (main person) or valid relationId (family member)
                            if (value === null || value === undefined) {
                                return t('validations.required', {
                                    field: t('tasks.linkTo')
                                });
                            }
                            // "main" is valid (main person selection)
                            if (value === 'main') {
                                return true;
                            }
                            // Any other string value is also valid (family member selection)
                            return true;
                        }
                    } }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'activityType', label: t('people.activityType') || 'Activity Type', options: [
                        { label: t('people.plannedActivities') || 'Planned Activities', value: 'planned' },
                        { label: t('people.makeshift') || 'Makeshift', value: 'makeshift' }
                    ], error: errors.activityType?.message, rules: {
                        required: t('validations.required', {
                            field: t('people.activityType') || 'Activity Type'
                        })
                    } }), selectedActivityType === 'planned' && (_jsx(SingleSelect, { bg: 'grey8', control: control, name: 'activityId', label: t('people.plannedActivity'), options: peopleActivitiesByPersonId
                        ?.filter((activity) => {
                        // Always include the current activity if we're in edit mode
                        if (editMode && taskData && activity.id === taskData.activityId) {
                            return true;
                        }
                        if (!selectedFamilyMember || selectedFamilyMember === 'main') {
                            // If no family member selected or main person, show activities with null relationId
                            return !activity.relationId;
                        }
                        else {
                            // If family member selected, show activities with matching relationId
                            return activity.relationId === selectedFamilyMember;
                        }
                    })
                        ?.map((activity) => ({
                        label: activity.activity,
                        value: activity.id
                    })) || [], error: errors.activityId?.message, loading: peopleActivitiesStatus === APIStatus.PENDING, rules: {
                        required: selectedActivityType === 'planned'
                            ? t('validations.required', {
                                field: t('people.plannedActivity')
                            })
                            : undefined
                    } })), selectedActivityType === 'makeshift' && (_jsx(SingleSelect, { bg: 'grey8', control: control, name: 'manualActivityType', label: t('people.activities'), options: Object.values(ManualActivityTypeEnum)
                        .filter((value) => typeof value === 'number')
                        .map((value) => ({
                        label: ManualActivityTypeEnumDisplay[value],
                        value: value
                    })), error: errors.manualActivityType?.message, rules: {
                        required: selectedActivityType === 'makeshift'
                            ? t('validations.required', { field: t('people.activities') })
                            : undefined
                    } })), _jsx(ControlledHCheckboxAutocomplete, { bg: 'grey8', control: control, name: 'assignedTo', label: t('tasks.assignTo'), options: usersWithoutPagination
                        ?.filter((user) => user.isActive)
                        ?.map((user) => ({
                        label: `${user.firstName} ${user.lastName}`,
                        value: user.id
                    })) || [], error: errors.assignedTo?.message, loading: usersStatus === APIStatus.PENDING, maxTags: 3, showDesignation: false, disabled: isCollaborator, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll') }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'priority', label: t('tasks.priority'), options: [
                        {
                            label: t(getPriorityTranslationKey(PriorityEnum.High)),
                            value: PriorityEnum.High,
                            icon: getPriorityIcon(PriorityEnum.High)
                        },
                        {
                            label: t(getPriorityTranslationKey(PriorityEnum.Medium)),
                            value: PriorityEnum.Medium,
                            icon: getPriorityIcon(PriorityEnum.Medium)
                        },
                        {
                            label: t(getPriorityTranslationKey(PriorityEnum.Low)),
                            value: PriorityEnum.Low,
                            icon: getPriorityIcon(PriorityEnum.Low)
                        }
                    ], error: errors.priority?.message, rules: {
                        required: t('validations.required', { field: t('tasks.priority') }),
                        validate: (value) => {
                            if (value === undefined || value === null || value === PriorityEnum.None) {
                                return t('validations.required', {
                                    field: t('tasks.priority')
                                });
                            }
                            return true;
                        }
                    } }), isMobile && (_jsxs(Box, { sx: { display: 'flex', gap: 2, mt: 2 }, children: [_jsx(Button, { fullWidth: true, onClick: onClose, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', type: 'submit', onClick: () => {
                                const form = formRef.current;
                                if (form) {
                                    form.requestSubmit();
                                }
                            }, children: editMode ? t('actions.saveChanges') : t('actions.createNow') })] }))] }) }, editMode ? taskData?.id : 'new'));
    if (isMobile) {
        return (_jsx(BDrawer, { open: open, onClose: onClose, title: title, children: formContent }));
    }
    return (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: onClose, titleWithIcon: title, titleIcon: '/assets/images/NewActivityTask.png', maxWidth: 'sm', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, onClick: onClose, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: () => {
                        const form = formRef.current;
                        if (form) {
                            form.requestSubmit();
                        }
                    }, children: editMode ? t('actions.saveChanges') : t('actions.createNow') })] }), children: formContent }));
};
export default AddNewActivityTaskDialog;
